import React from 'react';
import { Calendar, Clock } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

const EventHeader = ({ event, language: languageProp }) => {
  const { t, language: contextLanguage } = useLanguage();
  const language = languageProp || contextLanguage;

  // Get event status
  const getEventStatus = () => {
    const now = new Date();
    const startDate = event.start_date ? new Date(event.start_date) : null;
    const endDate = event.end_date ? new Date(event.end_date) : null;

    if (!startDate || isNaN(startDate.getTime())) {
      return { type: 'unknown', label: '', isLive: false, isPast: false };
    }

    if (!endDate || isNaN(endDate.getTime())) {
      if (now < startDate) {
        return { type: 'upcoming', label: '', isLive: false, isPast: false };
      }
      return { type: 'live', label: 'LIVE', isLive: true, isPast: false };
    }

    const endOfDay = new Date(endDate);
    endOfDay.setHours(23, 59, 59, 999);

    if (now > endOfDay) {
      return { type: 'past', label: t('events.ended'), isLive: false, isPast: true };
    } else if (now >= startDate && now <= endOfDay) {
      return { type: 'live', label: 'LIVE', isLive: true, isPast: false };
    } else {
      return { type: 'upcoming', label: '', isLive: false, isPast: false };
    }
  };

  // Format date range
  const formatDateRange = () => {
    if (!event.start_date || !event.end_date) return t('events.tba');

    try {
      const start = new Date(event.start_date);
      const end = new Date(event.end_date);

      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        return `${event.start_date} - ${event.end_date}`;
      }

      if (language === 'th') {
        const startMonth = start.toLocaleDateString('th-TH', { month: 'long', day: 'numeric', year: 'numeric' });
        const endMonth = end.toLocaleDateString('th-TH', { month: 'long', day: 'numeric', year: 'numeric' });

        if (start.getTime() === end.getTime()) {
          return startMonth;
        } else {
          return `${startMonth} - ${endMonth}`;
        }
      } else {
        const startMonth = start.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
        const endMonth = end.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

        if (start.getTime() === end.getTime()) {
          return startMonth;
        } else {
          return `${startMonth} - ${endMonth}`;
        }
      }
    } catch (e) {
      return `${event.start_date} - ${event.end_date}`;
    }
  };

  // Format time range
  const formatTimeRange = () => {
    if (!event.start_time || !event.end_time) return null;
    return `${event.start_time.substring(0, 5)} - ${event.end_time.substring(0, 5)}`;
  };

  const status = getEventStatus();
  const title = language === 'th' 
    ? (event.title_th || event.name_th || event.title || event.name_en || 'ไม่ระบุชื่อ')
    : (event.title_en || event.name_en || event.title || event.title_th || 'Untitled Event');

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
      {/* Status Badge */}
      {status.isLive && (
        <div className="inline-flex items-center gap-2 bg-green-500 text-white px-4 py-2 rounded-full font-bold mb-4 animate-pulse">
          <span className="w-2 h-2 bg-white rounded-full"></span>
          {status.label}
        </div>
      )}
      {status.isPast && (
        <div className="inline-flex items-center gap-2 bg-gray-500 text-white px-4 py-2 rounded-full font-bold mb-4">
          {status.label}
        </div>
      )}

      {/* Title */}
      <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
        {title}
      </h1>

      {/* Date Range */}
      <div className="flex items-center gap-2 text-gray-600 mb-2">
        <Calendar className="w-5 h-5" />
        <span className="text-lg font-medium">{formatDateRange()}</span>
      </div>

      {/* Time Range */}
      {formatTimeRange() && (
        <div className="flex items-center gap-2 text-gray-600">
          <Clock className="w-5 h-5" />
          <span className="text-lg">{formatTimeRange()}</span>
        </div>
      )}
    </div>
  );
};

export default EventHeader;
